// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the app's end of a live terminal session (konvo_api agents/AgentsTerminalSessions.ts, the
// frames in @konvo/schema AgentTerminals.ts "Terminal sessions"). One link per machine: a WebSocket
// to Konvo's relay, which the machine's bridge joins. It says hello with this app's id, follows the
// chats whose terminals are shown, attaches the terminals on screen and carries what is typed.
//
// Typing survives a dropped connection without doubling: every input frame carries a number counting
// up per terminal; the machine acks each and skips numbers it has already written, and on reconnect
// this sends again everything not acked yet, in order. Reconnects come at once after a session's
// longest life (4014) and with a growing pause after a drop; turning terminals off (4013) or losing
// access (4016) ends the link for good.
//
// No app imports: the API's end-to-end test drives this very file against the real relay and bridge.

import type {AgentTerminalClientFrame, AgentTerminalServerFrame} from '@konvo/schema/src/domains/konvo/AgentTerminals';

/** The browser WebSocket API, as far as this uses it. */
export interface LinkSocket {
	readonly readyState: number;
	send(data: string): void;
	close(code?: number, reason?: string): void;
	onopen: ((event: unknown) => void) | null;
	onmessage: ((event: {data: unknown}) => void) | null;
	onclose: ((event: {code: number; reason: string}) => void) | null;
	onerror: ((event: unknown) => void) | null;
}

export type LinkStatus = 'connecting' | 'ready' | 'lost' | 'failed' | 'closed';

export interface LinkProblem {
	code: string;
	message: string;
}

export interface TerminalLinkOptions {
	/** A fresh session and a socket opened with it (each connect asks Konvo for a new one-use token). */
	connect: () => Promise<LinkSocket>;
	/** This app's id, kept across reconnects so the machine knows input it already has. */
	cid: string;
	onFrame: (frame: AgentTerminalServerFrame) => void;
	onStatus: (status: LinkStatus, problem?: LinkProblem) => void;
	/** Test hooks. */
	retryMs?: (attempt: number) => number;
	maxAttempts?: number;
}

/** Characters per input frame (the machine refuses more; a paste goes in several). */
export const INPUT_CHUNK = 8 * 1024;
/** Unacked input kept per terminal for a resend; past this the oldest is dropped. */
const UNACKED_MAX = 512;
const CLOSE = {turnedOff: 4013, expired: 4014, noAccess: 4016};

const keyOf = (threadId: string, id: string) => `${threadId}\u0000${id}`;

interface Pending {
	resolve: (frame: AgentTerminalServerFrame) => void;
	reject: (problem: LinkProblem) => void;
}

interface Typed {
	threadId: string;
	id: string;
	next: number;
	unacked: Array<{seq: number; data: string}>;
}

export class TerminalLink {
	private readonly options: TerminalLinkOptions;
	private socket: LinkSocket | null = null;
	private ready = false;
	private disposed = false;
	private attempts = 0;
	private retryTimer: ReturnType<typeof setTimeout> | null = null;
	status: LinkStatus = 'connecting';
	/** Chats followed and terminals attached: sent again on every (re)connect. */
	private readonly follows = new Set<string>();
	private readonly attached = new Map<string, {threadId: string; id: string}>();
	private readonly typed = new Map<string, Typed>();
	/** The last size asked for each Konvo terminal, sent again on reconnect. */
	private readonly sizes = new Map<string, {threadId: string; id: string; cols: number; rows: number}>();
	private readonly pending = new Map<string, Pending>();
	private nextReq = 1;
	/** Frames before the machine is in. */
	private queue: Array<AgentTerminalClientFrame> = [];

	constructor(options: TerminalLinkOptions) {
		this.options = options;
		void this.open();
	}

	get isReady() {
		return this.ready;
	}

	follow(threadId: string) {
		this.follows.add(threadId);
		this.send({t: 'follow', threadId});
	}

	attach(threadId: string, id: string) {
		this.attached.set(keyOf(threadId, id), {threadId, id});
		this.send({t: 'attach', threadId, id});
	}

	detach(threadId: string, id: string) {
		if (!this.attached.delete(keyOf(threadId, id))) return;
		this.send({t: 'detach', threadId, id});
	}

	/** Types into a terminal: split into frames, numbered, kept until the machine acks them. */
	input(threadId: string, id: string, data: string) {
		if (!data) return;
		const key = keyOf(threadId, id);
		let typed = this.typed.get(key);
		if (!typed) {
			typed = {threadId, id, next: 1, unacked: []};
			this.typed.set(key, typed);
		}
		for (let i = 0; i < data.length; i += INPUT_CHUNK) {
			const part = data.slice(i, i + INPUT_CHUNK);
			const seq = typed.next++;
			typed.unacked.push({seq, data: part});
			if (typed.unacked.length > UNACKED_MAX) typed.unacked.shift();
			this.send({t: 'in', threadId, id, seq, data: part});
		}
	}

	resize(threadId: string, id: string, cols: number, rows: number) {
		const key = keyOf(threadId, id);
		const before = this.sizes.get(key);
		if (before && before.cols === cols && before.rows === rows) return;
		this.sizes.set(key, {threadId, id, cols, rows});
		this.send({t: 'resize', threadId, id, cols, rows});
	}

	/** A new terminal for the chat, at this size: resolves with its id. */
	openTerminal(threadId: string, cols: number, rows: number): Promise<string> {
		return this.request((req) => ({t: 'open', req, threadId, cols, rows})).then((frame) =>
			frame.t === 'opened' ? frame.id : Promise.reject({code: 'BAD_ANSWER', message: 'The machine did not open it.'}),
		);
	}

	closeTerminal(threadId: string, id: string): Promise<void> {
		return this.request((req) => ({t: 'close', req, threadId, id})).then(() => {
			this.detach(threadId, id);
			this.sizes.delete(keyOf(threadId, id));
		});
	}

	/** Connect again now (after it failed, or the person asked). */
	retry() {
		if (this.disposed) return;
		this.attempts = 0;
		if (this.retryTimer) clearTimeout(this.retryTimer);
		this.retryTimer = null;
		if (!this.socket) void this.open();
	}

	dispose() {
		this.disposed = true;
		if (this.retryTimer) clearTimeout(this.retryTimer);
		this.failPending({code: 'CLOSED', message: 'Closed.'});
		const socket = this.socket;
		this.socket = null;
		if (socket) {
			socket.onclose = null;
			socket.onmessage = null;
			try {
				socket.close(1000, 'Closed.');
			} catch {}
		}
		this.setStatus('closed');
	}

	private request(make: (req: string) => AgentTerminalClientFrame): Promise<AgentTerminalServerFrame> {
		if (!this.ready) return Promise.reject({code: 'NOT_CONNECTED', message: 'Not connected to the machine yet.'});
		const req = `r${this.nextReq++}`;
		return new Promise((resolve, reject) => {
			this.pending.set(req, {resolve, reject});
			this.send(make(req));
		});
	}

	private send(frame: AgentTerminalClientFrame) {
		if (this.ready && this.socket && this.socket.readyState === 1) this.socket.send(JSON.stringify(frame));
		else if (
			frame.t !== 'in' &&
			frame.t !== 'resize' &&
			frame.t !== 'follow' &&
			frame.t !== 'attach' &&
			frame.t !== 'detach'
		)
			this.queue.push(frame);
		// Input, sizes, follows and attaches go again from their own records once connected.
	}

	private setStatus(status: LinkStatus, problem?: LinkProblem) {
		this.status = status;
		this.options.onStatus(status, problem);
	}

	private async open() {
		if (this.disposed) return;
		this.ready = false;
		this.setStatus(this.attempts === 0 ? 'connecting' : 'lost');
		let socket: LinkSocket;
		try {
			socket = await this.options.connect();
		} catch (error) {
			const problem = asProblem(error);
			// Refusals that waiting won't fix (off, read-only, too old, not yours) end the link.
			if (FINAL.has(problem.code)) return this.fail(problem);
			return this.later(problem);
		}
		if (this.disposed) {
			socket.close(1000, 'Closed.');
			return;
		}
		this.socket = socket;
		socket.onmessage = (event) => {
			if (typeof event.data !== 'string') return;
			let frame: AgentTerminalServerFrame;
			try {
				frame = JSON.parse(event.data) as AgentTerminalServerFrame;
			} catch {
				return;
			}
			this.receive(frame);
		};
		socket.onopen = () => {
			// Hello goes at once; the rest waits for the machine to be in.
			socket.send(JSON.stringify({t: 'hello', v: 1, cid: this.options.cid} satisfies AgentTerminalClientFrame));
		};
		socket.onclose = (event) => {
			if (this.socket !== socket) return;
			this.socket = null;
			this.ready = false;
			this.failPending({code: 'LOST', message: 'The connection to the machine dropped.'});
			if (this.disposed) return;
			const problem = {code: `CLOSE_${event.code}`, message: event.reason || 'The connection ended.'};
			if (event.code === CLOSE.turnedOff || event.code === CLOSE.noAccess) return this.fail(problem);
			if (event.code === CLOSE.expired) {
				this.attempts = 0;
				void this.open();
				return;
			}
			this.later(problem);
		};
		socket.onerror = () => {};
		if (socket.readyState === 1) socket.onopen?.({});
	}

	private later(problem: LinkProblem) {
		this.attempts++;
		const max = this.options.maxAttempts ?? 8;
		if (this.attempts > max) return this.fail(problem);
		this.setStatus('lost', problem);
		const wait = (this.options.retryMs ?? ((n) => Math.min(10_000, 400 * 2 ** (n - 1))))(this.attempts);
		this.retryTimer = setTimeout(() => {
			this.retryTimer = null;
			void this.open();
		}, wait);
	}

	private fail(problem: LinkProblem) {
		this.setStatus('failed', problem);
	}

	private failPending(problem: LinkProblem) {
		const pending = [...this.pending.values()];
		this.pending.clear();
		for (const p of pending) p.reject(problem);
		this.queue = this.queue.filter((f) => f.t !== 'open' && f.t !== 'close');
	}

	private receive(frame: AgentTerminalServerFrame) {
		if (frame.t === 'relay' && frame.state === 'ready') {
			this.ready = true;
			this.attempts = 0;
			this.setStatus('ready');
			this.replay();
			return;
		}
		if (frame.t === 'ack') {
			const typed = this.typed.get(keyOf(frame.threadId, frame.id));
			if (typed) typed.unacked = typed.unacked.filter((u) => u.seq > frame.seq);
		}
		if (frame.t === 'error' && frame.seq !== undefined && frame.threadId && frame.id) {
			// A write the machine could not do is not sent again.
			const typed = this.typed.get(keyOf(frame.threadId, frame.id));
			if (typed) typed.unacked = typed.unacked.filter((u) => u.seq !== frame.seq);
		}
		if ((frame.t === 'opened' || frame.t === 'closed' || frame.t === 'error') && frame.req) {
			const pending = this.pending.get(frame.req);
			if (pending) {
				this.pending.delete(frame.req);
				if (frame.t === 'error') pending.reject({code: frame.code, message: frame.message});
				else pending.resolve(frame);
			}
		}
		this.options.onFrame(frame);
	}

	/** The machine is in (again): what this app follows, its terminals, sizes and unacked input. */
	private replay() {
		const socket = this.socket;
		if (!socket) return;
		const out = (frame: AgentTerminalClientFrame) => socket.send(JSON.stringify(frame));
		for (const threadId of this.follows) out({t: 'follow', threadId});
		for (const {threadId, id} of this.attached.values()) out({t: 'attach', threadId, id});
		for (const size of this.sizes.values()) out({t: 'resize', ...size});
		for (const typed of this.typed.values())
			for (const u of typed.unacked) out({t: 'in', threadId: typed.threadId, id: typed.id, seq: u.seq, data: u.data});
		const queued = this.queue;
		this.queue = [];
		for (const frame of queued) out(frame);
	}
}

/** Refusals no reconnect fixes. */
const FINAL = new Set([
	'TERMINALS_OFF',
	'MACHINE_READ_ONLY',
	'BRIDGE_TOO_OLD',
	'NOT_FOUND',
	'AGENTS_NOT_ENABLED',
	'UNSUPPORTED',
]);

function asProblem(error: unknown): LinkProblem {
	if (error && typeof error === 'object') {
		const e = error as {code?: unknown; message?: unknown; body?: {code?: unknown; message?: unknown}};
		const code = typeof e.body?.code === 'string' ? e.body.code : typeof e.code === 'string' ? e.code : 'ERROR';
		const message =
			typeof e.body?.message === 'string' ? e.body.message : typeof e.message === 'string' ? e.message : 'Failed.';
		return {code, message};
	}
	return {code: 'ERROR', message: String(error)};
}

/** Keys the phone's key row sends (as a terminal sends them). */
export const KEY_SEQUENCES = {
	ctrlC: '\u0003',
	tab: '\t',
	esc: '\u001b',
	up: '\u001b[A',
	down: '\u001b[B',
	right: '\u001b[C',
	left: '\u001b[D',
	enter: '\r',
} as const;

/** Ctrl with a key, as a terminal sends it (letters and a few signs); null when there is none. */
export function ctrlKey(key: string): string | null {
	if (key.length !== 1) return null;
	const c = key.toLowerCase();
	if (c >= 'a' && c <= 'z') return String.fromCharCode(c.charCodeAt(0) - 96);
	const signs: Record<string, string> = {
		'@': '\u0000',
		'[': '\u001b',
		'\\': '\u001c',
		']': '\u001d',
		'^': '\u001e',
		_: '\u001f',
		' ': '\u0000',
	};
	return signs[c] ?? null;
}
